import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import lottie from "lottie-web";
import { Helmet } from "react-helmet-async";

import Post from "../components/Post";
import Footer from "../components/Footer";
import CategoryStrip from "../components/CategoryStrip";
import BleepSpinnerDark from "../components/lottie/BleepSpinnerDark.json";

const PAGE_SIZE = 12;

const truncate = (s, n = 40) => (s.length > n ? `${s.slice(0, n)}…` : s);

// Results for a query. Same furniture as /bleeps -- dark band, title block,
// category strip, one grid -- so moving between the two does not feel like
// moving between two products.
const Search = ({ domain }) => {
  const navigate = useNavigate();
  const location = useLocation();

  /* ---------------- The URL is the query --------------------------------- */
  // Derived, never mirrored into state. The category strip and the navbar's
  // suggestions both link here with <Link>, so arriving at /search from /search
  // does not remount the page; anything held in state would go stale.
  const params = new URLSearchParams(location.search);
  const searchTerm = params.get("q") || "";
  const category = params.get("category") || "";

  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const fetchingRef = useRef(false);
  const spinnerRef = useRef(null);

  const buildUrl = useCallback(
    (pageNumber) =>
      `/api/posts/search?searchterm=${encodeURIComponent(searchTerm)}` +
      (category ? `&category=${encodeURIComponent(category)}` : "") +
      `&page=${pageNumber}&size=${PAGE_SIZE}`,
    [searchTerm, category],
  );

  /* ---------------- First page, refetched whenever the query changes ------ */
  useEffect(() => {
    if (!searchTerm.trim() && !category.trim()) {
      setPosts([]);
      setPage(1);
      setHasMore(false);
      setIsLoading(false);
      return;
    }

    // Typing a term while a category is already loading used to let the slower
    // response land last and overwrite the newer one.
    const controller = new AbortController();

    setPosts([]);
    setPage(2);
    setHasMore(true);
    setIsLoading(true);
    fetchingRef.current = true;

    (async () => {
      try {
        const res = await fetch(buildUrl(1), { signal: controller.signal });
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setPosts(list);
        setHasMore(list.length === PAGE_SIZE);
      } catch (err) {
        if (err.name === "AbortError") return; // a newer query owns the state
        console.error("Error fetching posts:", err);
        setHasMore(false);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          fetchingRef.current = false;
        }
      }
    })();

    return () => controller.abort();
  }, [buildUrl, searchTerm, category]);

  /* ---------------- Subsequent pages -------------------------------------- */
  const fetchMore = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      const res = await fetch(buildUrl(page));
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setPosts((prev) => [...prev, ...list]);
      setHasMore(list.length === PAGE_SIZE);
      setPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error fetching posts:", err);
      setHasMore(false);
    } finally {
      fetchingRef.current = false;
    }
  };

  /* ---------------- Spinner ------------------------------------------------ */
  // No dependency array: the container only exists once InfiniteScroll renders
  // its loader, which is well after mount.
  useEffect(() => {
    if (spinnerRef.current && !spinnerRef.current._loaded) {
      spinnerRef.current._loaded = true;
      lottie.loadAnimation({
        container: spinnerRef.current,
        renderer: "canvas",
        loop: true,
        autoplay: true,
        animationData: BleepSpinnerDark,
      });
    }
  });

  /* ---------------- Query changes ----------------------------------------- */
  // There is no field on this page: the navbar's search bar is the only one,
  // and it is present on every route including the mobile panel. New terms
  // arrive as a URL change, which is all this page reads.
  const clearCategory = () => {
    const next = new URLSearchParams();
    if (searchTerm) next.set("q", searchTerm);
    navigate({ pathname: "/search", search: next.toString() });
  };

  const subLine =
    searchTerm && category ? (
      <>
        Matching <b>"{truncate(searchTerm)}"</b> in <b>{category}</b>.
      </>
    ) : searchTerm ? (
      <>
        Bleeps matching <b>"{truncate(searchTerm)}"</b>.
      </>
    ) : category ? (
      <>
        Bleeps reported in <b>{category}</b>.
      </>
    ) : (
      "Search from the bar above, or pick a sector to browse."
    );

  const hasQuery = Boolean(searchTerm || category);

  return (
    <div className="bleep-light bl-page bl-page--dark">
      <Helmet>
        <title>Search Bleeps | BLEEP</title>
        <meta
          name="description"
          content="Find bleep reports that match your search. Filter, browse, and open details."
        />
      </Helmet>

      <section className="bl-section bl-section--dark">
        <div className="bl-shell bl-feed-head">
          <div className="bl-head-row">
            <h1 className="bl-page-title">Search</h1>
            {/* Home rather than navigate(-1), matching /bleeps: search is
                reached from the navbar on any page, so "back" could be
                anywhere, and results are a place you leave rather than a step
                in a flow you would retrace. */}
            <button
              type="button"
              className="bl-close-btn"
              onClick={() => navigate("/")}
              aria-label="Close search and go to the homepage"
            >
              <i
                className="fas fa-xmark"
                aria-hidden="true"
              />
            </button>
          </div>
          <p className="bl-page-sub">{subLine}</p>

          {category && (
            <div className="bl-filter-row">
              <button
                type="button"
                className="bl-filter-chip"
                onClick={clearCategory}
                title={`Clear the ${category} filter`}
              >
                <i
                  className="fas fa-tag"
                  aria-hidden="true"
                />
                {category}
                <i
                  className="fas fa-times"
                  aria-hidden="true"
                />
              </button>
            </div>
          )}
        </div>

        {/* The strip is the browse affordance, so the empty states no longer
            repeat the full category list underneath them. */}
        <div className="bl-strip-wrap">
          <CategoryStrip />
        </div>

        <div className="bl-shell bl-shell--spaced">
          {isLoading && posts.length === 0 ? (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <div
                  className="col pb-3"
                  key={i}
                >
                  <div className="bleep-post-skeleton bl-pulse">
                    <div className="bleep-post-skeleton__media" />
                    <div className="bleep-post-skeleton__body">
                      <div className="bleep-post-skeleton__line" />
                      <div
                        className="bleep-post-skeleton__line"
                        style={{ width: "80%" }}
                      />
                      <div
                        className="bleep-post-skeleton__line"
                        style={{ width: "45%" }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="bl-empty">
              <div className="bl-empty__icon">
                <i
                  className={hasQuery ? "fas fa-magnifying-glass" : "fas fa-bolt"}
                  aria-hidden="true"
                />
              </div>
              <h2 className="bl-empty__title">
                {hasQuery ? "No bleeps found" : "Find a bleep"}
              </h2>
              <p className="bl-empty__text">
                {searchTerm && category
                  ? `Nothing matched "${truncate(searchTerm)}" in ${category}. Try a different term, or pick another sector above.`
                  : searchTerm
                    ? `Nothing matched "${truncate(searchTerm)}". Try a different term, or pick a sector above.`
                    : category
                      ? `No bleeps have been reported in ${category} yet. Pick another sector above.`
                      : "Search by keyword, product, city or address from the bar at the top of the page, or pick a sector above to browse."}
              </p>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={posts.length}
              next={fetchMore}
              hasMore={hasMore}
              style={{ overflow: "unset" }}
              loader={
                <div
                  style={{ display: "flex", justifyContent: "center" }}
                  aria-label="Loading more results"
                >
                  <div
                    ref={spinnerRef}
                    style={{ width: 140, height: 140 }}
                  />
                </div>
              }
              endMessage={
                <p className="bl-end-message">
                  That's every match <i className="fas fa-check" />
                </p>
              }
            >
              <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
                {posts.map((post) => (
                  <Post
                    key={post._id}
                    post={post}
                  />
                ))}
              </div>
            </InfiniteScroll>
          )}
        </div>
      </section>

      <Footer domain={domain} />
    </div>
  );
};

export default Search;
